import { useEffect, useState } from "react";

import { css } from "@compiled/react";
import { FormattedMessage } from "react-intl";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import Lozenge from "@atlaskit/lozenge";
import { media } from "@atlaskit/primitives";

import { PricingPeriod, TRACKING_EVENT_NAMES } from "../constants";
import { usePriceFinderStore } from "../store/store";

const teamSizeStyles = css({
    fontFamily: "var(--bxp-charlie-text-font-family)",
    fontSize: "1rem",
    letterSpacing: ".3px",
    fontWeight: "600",
    marginInlineEnd: "10px"
});

const inputTermStyle = css({
    fontFamily: "var(--bxp-charlie-text-font-family)",
    fontSize: "1rem",
    letterSpacing: ".3px",
    fontWeight: "400",
    flex: 1
});

const radioLabelStyles = css({
    cursor: "pointer",
    gap: "8px",
    display: "flex",
    alignItems: "center"
});

const radioStyles = css({
    "backgroundColor": "#fafbfc",
    "border": "calc(2px * 12 / 7) solid #DFE1E6",
    "borderRadius": "50%",
    "cursor": "pointer",
    "display": "flex",
    "width": "24px",
    "height": "24px",
    "margin": 0,
    "position": "relative",
    "alignItems": "center",
    "justify-content": "center",
    "flexShrink": 0,
    "outline": "none",
    "transform": "scale(calc(.583333))",
    "transition":
        "border-color 0.2s ease-in-out 0s, background-color 0.2s ease-in-out 0s",
    "verticalAlign": "top",
    "appearance": "none",
    ":focus": {
        borderColor: "#4c9aff"
    },
    ":active, :hover": {
        backgroundColor: "#EBECF0"
    },
    "::after": {
        width: "calc(6.85714px)",
        height: "calc(6.85714px)",
        position: "absolute",
        background: "#fafbfc",
        borderRadius: "50%",
        content: "",
        opacity: 0,
        transition:
            "background-color 0.2s ease-in-out 0s, opacity 0.2s ease-in-out 0s"
    },
    ":checked": {
        "borderColor": "#0052cc",
        "backgroundColor": "#0052cc",
        ":hover": {
            backgroundColor: "#0065ff"
        },
        "::after": {
            opacity: 1
        }
    },
    [media.above.sm]: {
        "backgroundColor": "#fff",
        "borderColor": "#0052cc",
        "borderWidth": "2px",
        "transform": "none",
        ":focus-visible": {
            boxShadow: "#0052cc 0px 0px 3px 3px"
        },
        "::after": {
            width: "14px",
            height: "14px",
            backgroundColor: "#0052cc"
        },
        ":checked": {
            "backgroundColor": "#fff",
            ":hover": {
                backgroundColor: "unset"
            }
        }
    }
});

const discountLozengeStyles = css({
    width: "fit-content",
    margin: "auto",
    paddingInlineStart: "4px",
    textTransform: "uppercase",
    [media.below.sm]: {
        maxWidth: "220px",
        width: "100%",
        margin: "0",
        marginInlineStart: "16px",
        textAlign: "right"
    }
});

const billMeStyles = css({
    height: "100%",
    display: "flex",

    [media.below.xs]: {
        flexWrap: "wrap"
    }
});

interface PriceFinderInputsRedesignProps {
    isMonthlyBillingOnly: boolean;
}

export const PriceFinderInputsRedesign = (
    props: PriceFinderInputsRedesignProps
) => {
    const { createAnalyticsEvent } = useAnalyticsEvents();

    const { isMonthlyBillingOnly } = props;

    const [{ unitCount, billingCycle, messages }, actions] =
        usePriceFinderStore();

    const [visualUnitCount, setVisualUnitCount] = useState<number | "">(
        unitCount
    );

    useEffect(() => {
        if (
            visualUnitCount !== "" &&
            visualUnitCount > 0 &&
            !Number.isNaN(visualUnitCount)
        ) {
            actions.updateUnitCount(visualUnitCount);
        }
    }, [visualUnitCount, actions]);

    const handleUnitCountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const stringValue = e.target.value;

        if (stringValue === "") return setVisualUnitCount("");

        // validate max number of digits
        // default is 5
        // TODO: make this configurable
        if (stringValue.length > 5) return;

        const numberValue = parseInt(stringValue, 10);

        if (numberValue < 1) return;
        setVisualUnitCount(numberValue);
    };

    const handleUnitCountBlur = () => {
        if (visualUnitCount === "") setVisualUnitCount(unitCount);
        const data = { unitCount };
        createAnalyticsEvent({
            eventType: "ui",
            eventName: `${TRACKING_EVENT_NAMES.unitTierBox}_blurred`,
            eventComponent: "input",
            event: "blurred",
            actionSubject: TRACKING_EVENT_NAMES.unitTierBox,
            action: "blurred",
            attributes: data,
            data
        }).fire();
    };

    const handleRadioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        actions.updateBillingCycle(e.target.id as PricingPeriod);
    };

    return (
        <div
            css={css({
                margin: "auto",
                maxWidth: "1408px"
            })}
        >
            <div
                css={css({
                    maxWidth: "400px",
                    margin: "auto",
                    [media.above.sm]: {
                        maxWidth: "100%"
                    }
                })}
            >
                <div
                    css={css({
                        maxWidth: "400px",
                        [media.above.sm]: {
                            justifyContent: "space-between",
                            display: "flex",
                            maxWidth: "100%"
                        }
                    })}
                >
                    <div
                        css={css({
                            display: "flex",
                            alignItems: "center",
                            maxWidth: "300px",
                            [media.above.sm]: {
                                display: "block"
                            }
                        })}
                    >
                        <label css={teamSizeStyles} htmlFor="unit-count">
                            <FormattedMessage
                                {...messages.unitCountInputTextRedesign}
                            />
                        </label>
                        <div
                            css={css({
                                padding: "16px 0",
                                justifyContent: "center",
                                alignItems: "center",
                                width: "fit-content",
                                [media.above.sm]: {
                                    margin: 0,
                                    padding: 0,
                                    display: "inline-flex",
                                    flex: 1
                                }
                            })}
                        >
                            <input
                                id="unit-count"
                                type="number"
                                css={css({
                                    "color": "#172B4D",
                                    "fontWeight": "400",
                                    "fontSize": "1rem",
                                    "lineHeight": "24px",
                                    "marginInlineEnd": "5px",
                                    "padding": "8px 6px",
                                    "width": "65px",
                                    "borderRadius": "4px",
                                    "background": "#F4F5F7",
                                    "border": "none",
                                    "-moz-appearance": "textfield",
                                    "::-webkit-inner-spin-button, ::-webkit-outer-spin-button":
                                        {
                                            "-webkit-appearance": "none"
                                        }
                                })}
                                value={visualUnitCount}
                                onChange={handleUnitCountChange}
                                onBlur={handleUnitCountBlur}
                                data-event-component="inputNumber"
                                data-event-container="priceBlock"
                                data-event="clicked"
                            />
                        </div>
                        <label css={inputTermStyle} htmlFor="unit-term">
                            <FormattedMessage
                                {...messages.unitCountInputTerm}
                            />
                        </label>
                    </div>
                    {!isMonthlyBillingOnly && (
                        <>
                            <div
                                css={css({
                                    display: "flex",
                                    alignItems: "center"
                                })}
                            >
                                <span css={teamSizeStyles}>
                                    <FormattedMessage
                                        id="Pricing.priceFinderInputs.billMeText"
                                        defaultMessage="Bill me:"
                                    />
                                </span>
                                <span css={billMeStyles}>
                                    {/* False positive: the label is correctly associated with the input element */}
                                    {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                                    <label
                                        css={radioLabelStyles}
                                        htmlFor="MONTHLY"
                                    >
                                        <input
                                            css={radioStyles}
                                            type="radio"
                                            id="MONTHLY"
                                            name="billing"
                                            checked={billingCycle === "MONTHLY"}
                                            onChange={handleRadioChange}
                                        />
                                        <FormattedMessage
                                            id="Pricing.priceFinderInputs.monthly"
                                            defaultMessage="Monthly"
                                        />
                                    </label>
                                    {/* False positive: the label is correctly associated with the input element */}
                                    {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                                    <label
                                        css={[
                                            radioLabelStyles,
                                            css({ paddingInlineStart: "10px" })
                                        ]}
                                        htmlFor="ANNUAL"
                                    >
                                        <input
                                            css={radioStyles}
                                            type="radio"
                                            id="ANNUAL"
                                            name="billing"
                                            checked={billingCycle === "ANNUAL"}
                                            onChange={handleRadioChange}
                                        />
                                        <FormattedMessage
                                            id="Pricing.priceFinderInputs.anually"
                                            defaultMessage="Annually"
                                        />
                                    </label>
                                    <span css={discountLozengeStyles}>
                                        <Lozenge appearance="inprogress">
                                            <FormattedMessage
                                                id="Pricing.priceFinderInputs.saveUpTo"
                                                defaultMessage="SAVE UP TO 17%"
                                            />
                                        </Lozenge>
                                    </span>
                                </span>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};
